import uPlot from "uplot";
import { getSecondDateStrFromDateStr } from "./time";
import { INDEX_ANNOTATION_INDICATOR, INDEX_LAB_INDICATOR, INDEX_MED_INDICATOR, INDEX_OTHER_INDICATOR, TIMELINE_PLOT_DATA_OFFSET } from "../components/dashboard/DashboardDataParser";

export const PLOT_BLUE = "#2D78BF";
export const PLOT_PURPLE = "#503AB3";
export const PLOT_TEAL = "#3aa7b3";
export const PLOT_GREEN = "#52ad21";
export const PLOT_RED = "#DC267F";
export const PLOT_YELLOW = "#FFB000";
export const PLOT_GRAY = "#b5b5b5";


/**
 * Debounces the function to prevent too many occurrences in a short period of time
 * @param fn The function to debounce
 */
export function debounce(fn: any) {
    let raf: any;

    return (...args: any[]) => {
        if (raf)
            return;

        raf = requestAnimationFrame(() => {
            fn(...args);
            raf = null;
        });
    };
}

/**
 * Adds the event listener
 */
export function on(ev: any, el: any, fn: any) {
    el.addEventListener(ev, fn);
}

/**
 * Removes the event listener
 */
export function off(ev: any, el: any, fn: any) {
    el.removeEventListener(ev, fn);
}

/**
 * The uPlot plugin to render vertical lines on the plot
 * @param opts The uPlot options
 * @returns The uPlot plugin for annotations
 */
export function verticalAnnotationsPluginForTimeline(xMarks: any, opts: any) {
    function placeMark(u: any, mark: any, opts: any, showAnnotationLabel: boolean) {
        let markEl = document.createElement('div');
        markEl.classList.add('u-mark-x');

        let leftCss = Math.round(u.valToPos(mark.from, 'x'));
        let widthCss = mark.to > mark.from ? Math.round(u.valToPos(mark.to, 'x')) - leftCss : 0;

        Object.assign(markEl.style, {
            left:       `${leftCss}px`,
            width:      `${widthCss}px`,
            borderLeft: `${opts.width}px ${opts.dash ? 'dashed' : 'solid'} ${opts.stroke}`,
            borderRight: mark.to > mark.from ? `${opts.width}px ${opts.dash ? 'dashed' : 'solid'} ${opts.stroke}` : null,
            background:  opts.fill,
        });

        let labelEl = document.createElement('div');
        labelEl.classList.add('u-mark-x-label');
        if (!showAnnotationLabel) {
            labelEl.classList.add('u-mark-x-label-hidden')
        }
        labelEl.textContent = mark.label;
        labelEl.title = mark.descr;

        Object.assign(labelEl.style, {
            border:     `${opts.width}px ${opts.dash ? 'dashed' : 'solid'} ${opts.stroke}`,
            top: mark.label === "Dispo" || mark.label === "Roomed" ? '24px' : '0px',
            background: opts.fill,
            width: 'max-content',
        });

        markEl.appendChild(labelEl);
        u.over.appendChild(markEl);
    }

    return {
        hooks: {
            drawClear: [
                (u: any) => {
                    for (let el of u.over.querySelectorAll('.u-mark-x'))
                        el.remove();

                    xMarks.forEach((mark: any) => {
                        let o = opts.types[mark.type];
                        let showAnnotationLabel = opts.showAnnotationLabel;

                        if (o.show && (
                            mark.from >= u.scales.x.min &&
                            mark.from <= u.scales.x.max
                            ||
                            mark.to >= u.scales.x.min &&
                            mark.to <= u.scales.x.max
                            ||
                            mark.from <= u.scales.x.min &&
                            mark.to >= u.scales.x.max
                        )) {
                            placeMark(u, mark, o, showAnnotationLabel);
                        }
                    });
                }
            ],
        },
    };
}

/**
 * The uPlot plugin to render vertical lines on the plot
 * @param opts The uPlot options
 * @returns The uPlot plugin for annotations
 */
export function verticalAnnotationsPluginForNumerics(opts: any) {
    function placeMark(u: any, mark: any, opts: any, showAnnotationLabel: boolean, idx: number) {
        let markEl = document.createElement('div');
        markEl.classList.add('u-mark-x');

        let leftCss = Math.round(u.valToPos(mark.from, 'x'));
        let widthCss = mark.to > mark.from ? Math.round(u.valToPos(mark.to, 'x')) - leftCss : 0;

        Object.assign(markEl.style, {
            left:       `${leftCss}px`,
            width:      `${widthCss}px`,
            borderLeft: `${opts.width}px ${opts.dash ? 'dashed' : 'solid'} ${opts.stroke}`,
            borderRight: mark.to > mark.from ? `${opts.width}px ${opts.dash ? 'dashed' : 'solid'} ${opts.stroke}` : null,
            background:  opts.fill,
        });

        let labelEl = document.createElement('div');
        labelEl.classList.add('u-mark-x-label');
        if (!showAnnotationLabel) {
            labelEl.classList.add('u-mark-x-label-hidden')
        }
        labelEl.textContent = mark.label;
        labelEl.title = mark.descr;

        const flagBorderColor = mark.label.includes("↑") || mark.label.includes("↓") ? "red" : opts.stroke;
        Object.assign(labelEl.style, {
            border:     `${opts.width}px ${opts.dash ? 'dashed' : 'solid'} ${flagBorderColor}`,
            top: (idx % 5) * 20 + 'px',
            background: opts.fill,
            width: 'max-content',
        });

        markEl.appendChild(labelEl);
        u.over.appendChild(markEl);
    }

    return {
        hooks: {
            drawClear: [
                (u: any) => {
                    for (let el of u.over.querySelectorAll('.u-mark-x'))
                        el.remove();

                    globalThis.xAnnotationMarksForNumerics.forEach((mark: any, idx: number) => {
                        let o = opts.types[mark.type];
                        let showAnnotationLabel = opts.showAnnotationLabel;

                        if (o.show && (
                            mark.from >= u.scales.x.min &&
                            mark.from <= u.scales.x.max
                            ||
                            mark.to >= u.scales.x.min &&
                            mark.to <= u.scales.x.max
                            ||
                            mark.from <= u.scales.x.min &&
                            mark.to >= u.scales.x.max
                        )) {
                            placeMark(u, mark, o, showAnnotationLabel, idx);
                        }
                    });
                }
            ],
        },
    };
}

/**
 * The uPlot plugin to render vertical lines on the plot
 * @param opts The uPlot options
 * @returns The uPlot plugin for annotations
 */
export function verticalAnnotationsPluginForWaveforms(opts: any) {
    function placeMark(u: any, mark: any, opts: any, showAnnotationLabel: boolean) {
        let markEl = document.createElement('div');
        markEl.classList.add('u-mark-x');

        let leftCss = Math.round(u.valToPos(mark.from, 'x'));
        let widthCss = mark.to > mark.from ? Math.round(u.valToPos(mark.to, 'x')) - leftCss : 0;

        Object.assign(markEl.style, {
            left:       `${leftCss}px`,
            width:      `${widthCss}px`,
            borderLeft: `${opts.width}px ${opts.dash ? 'dashed' : 'solid'} ${opts.stroke}`,
            borderRight: mark.to > mark.from ? `${opts.width}px ${opts.dash ? 'dashed' : 'solid'} ${opts.stroke}` : null,
            background:  opts.fill,
        });

        let labelEl = document.createElement('div');
        labelEl.classList.add('u-mark-x-label');
        if (!showAnnotationLabel) {
            labelEl.classList.add('u-mark-x-label-hidden')
        }
        labelEl.textContent = mark.label;
        labelEl.title = mark.descr;

        Object.assign(labelEl.style, {
            border:     `${opts.width}px ${opts.dash ? 'dashed' : 'solid'} ${opts.stroke}`,
            top: mark.label === "Dispo" || mark.label === "Roomed" ? '24px' : '0px',
            background: opts.fill,
            width: 'max-content',
        });

        markEl.appendChild(labelEl);
        u.over.appendChild(markEl);
    }

    return {
        hooks: {
            drawClear: [
                (u: any) => {
                    for (let el of u.over.querySelectorAll('.u-mark-x'))
                        el.remove();

                    // console.log("refreshing waveform availability")
                    // console.log(globalThis.xAnnotationMarksForWaveforms)
                    globalThis.xAnnotationMarksForWaveforms.forEach((mark: any) => {
                        let o = opts.types[mark.type];
                        let showAnnotationLabel = opts.showAnnotationLabel;

                        if (o.show && (
                            mark.from >= u.scales.x.min &&
                            mark.from <= u.scales.x.max
                            ||
                            mark.to >= u.scales.x.min &&
                            mark.to <= u.scales.x.max
                            ||
                            mark.from <= u.scales.x.min &&
                            mark.to >= u.scales.x.max
                        )) {
                            placeMark(u, mark, o, showAnnotationLabel);
                        }
                    });
                }
            ],
        },
    };
}

/**
 * The uPlot plugin to render vertical lines on the plot
 * @param opts The uPlot options
 * @returns The uPlot plugin for annotations
 */
export function verticalAnnotationsPluginForFeatureImportance(opts: any) {
    function placeMark(u: any, mark: any, opts: any, showAnnotationLabel: boolean) {
        let markEl = document.createElement('div');
        markEl.classList.add('u-mark-x');

        let leftCss = Math.round(u.valToPos(mark.from, 'x'));
        let widthCss = mark.to > mark.from ? Math.round(u.valToPos(mark.to, 'x')) - leftCss : 0;

        Object.assign(markEl.style, {
            left:       `${leftCss}px`,
            width:      `${widthCss}px`,
            borderLeft: `${opts.width}px ${opts.dash ? 'dashed' : 'solid'} ${opts.stroke}`,
            borderRight: mark.to > mark.from ? `${opts.width}px ${opts.dash ? 'dashed' : 'solid'} ${opts.stroke}` : null,
            background:  opts.fill,
        });

        let labelEl = document.createElement('div');
        labelEl.classList.add('u-mark-x-label');
        if (!showAnnotationLabel) {
            labelEl.classList.add('u-mark-x-label-hidden')
        }
        labelEl.textContent = mark.label;
        labelEl.title = mark.descr;

        Object.assign(labelEl.style, {
            border:     `${opts.width}px ${opts.dash ? 'dashed' : 'solid'} ${opts.stroke}`,
            top: mark.label === "Dispo" || mark.label === "Roomed" ? '24px' : '0px',
            background: opts.fill,
            width: 'max-content',
        });

        markEl.appendChild(labelEl);
        u.over.appendChild(markEl);
    }

    return {
        hooks: {
            drawClear: [
                (u: any) => {
                    for (let el of u.over.querySelectorAll('.u-mark-x'))
                        el.remove();

                    // console.log("refreshing waveform availability")
                    // console.log(globalThis.xAnnotationMarksForWaveforms)
                    globalThis.xAnnotationMarksForFeatureImportance.forEach((mark: any) => {
                        let o = opts.types[mark.type];
                        let showAnnotationLabel = opts.showAnnotationLabel;

                        if (o.show && (
                            mark.from >= u.scales.x.min &&
                            mark.from <= u.scales.x.max
                            ||
                            mark.to >= u.scales.x.min &&
                            mark.to <= u.scales.x.max
                            ||
                            mark.from <= u.scales.x.min &&
                            mark.to >= u.scales.x.max
                        )) {
                            placeMark(u, mark, o, showAnnotationLabel);
                        }
                    });
                }
            ],
        },
    };
}



/**
 * The uPlot plugin to draw the points as a series of points
 * @param baseRadius The radius of the points
 * @returns The uPlot plugin
 */
export function seriesPointsPlugin({ baseRadius = 4 } = {}) {
    baseRadius *= devicePixelRatio;

    function drawTextBG(ctx: any, txt: any, x: any, y: any, color: string) {
        ctx.save();
        ctx.fillStyle = '#FFFFFF';
        // Using device pixel ratios will enable us to render the points
        // consistently on different screen resolutions
        ctx.font = `normal ${10 * window.devicePixelRatio}px Arial`;
        var width = ctx.measureText(txt).width;
        ctx.fillRect(x - width / 2, y - 8, width, 10);
        ctx.fillStyle = color;
        ctx.fillText(txt, x, y - 8);
        ctx.restore();
    }

    function drawCircle(ctx: any, cx: number, cy: number, color: string, textPreview: string) {
        drawTextBG(ctx, textPreview, cx - 10, cy + 30, color);

        ctx.beginPath();
        ctx.fillStyle = color + "AA"; // with opacity
        ctx.arc(cx, cy, baseRadius, 0, 2 * Math.PI, false);
        ctx.fill();
        ctx.closePath();
    }

    function drawTriangle(ctx: any, cx: number, cy: number, length: number, color: string, textPreview: string) {
        drawTextBG(ctx, textPreview, cx - 10, cy + 30, color);

        ctx.beginPath();
        ctx.fillStyle = color + "AA"; // with opacity
        ctx.moveTo(cx - length / 2, cy + length / 2);
        ctx.lineTo(cx + length / 2, cy + length / 2);
        ctx.lineTo(cx, cy - length / 2);
        ctx.lineTo(cx - length / 2, cy + length / 2);
        ctx.fill();
        ctx.closePath();
        // ctx.fillText(textPreview, cx - 10, cy + 30);
    }

    /**
     * Draws the points on the graph
     * @param u The uRanger plot 
     * @param i The series index being plotted
     * @param i0 The starting data index of each series being plotted
     * @param i1 The ending data index of each series being plotted
     */
    function drawPoints(u: any, i: any, i0: any, i1: any) {
        let { ctx } = u;
        let { _stroke, scale } = u.series[i];

        ctx.save();

        ctx.fillStyle = _stroke;

        let j = i0;

        while (j <= i1) {
            let val = u.data[i][j];
            if (val !== null) {
                let epochStartTime = u.data[0][j];
                let epochStartPos = Math.round(u.valToPos(epochStartTime, 'x', true));
                let cx = epochStartPos;
                let cy = Math.round(u.valToPos(val, scale, true));
                let metadata = u.data[i + TIMELINE_PLOT_DATA_OFFSET][j];
                if (!metadata) {
                    return;
                }
                let textPreview = metadata[1];
                if (i === INDEX_MED_INDICATOR) {
                    // Medication
                    drawCircle(ctx, cx, cy, PLOT_BLUE, textPreview);
                } else if (i === INDEX_LAB_INDICATOR) {
                    // Labs
                    drawTriangle(ctx, cx, cy, 16, PLOT_BLUE, textPreview);
                } else if (i === INDEX_ANNOTATION_INDICATOR) {
                    // User Annotation
                    drawCircle(ctx, cx, cy, PLOT_PURPLE, textPreview);
                } else if (i === INDEX_OTHER_INDICATOR) {
                    // Other
                    drawCircle(ctx, cx, cy, PLOT_GREEN, textPreview);
                }
            }
            j++;
        };

        ctx.restore();
    }

    return {
        opts: (u: any, opts: any) => {
            opts.series.forEach((s: any, i: any) => {
                if (i > 0) {
                    uPlot.assign(s, {
                        points: {
                            show: drawPoints,
                        }
                    });
                }
            });
        },
    };
}

/**
 * The uPlot plugin for the tooltip feature on the timeline plot
 */
// @ts-ignore
export function tooltipTimelinePlugin(addAnnotationToPlots: any, handleShowLabsModal: any, handleShowUserAnnotationModal: any, handleShowOtherAnnotationModal: any, handleShowContextMenu: any, patientVisit: PatientVisit, shiftX = 10, shiftY = 10) {
    let tooltipLeftOffset = 0;
    let tooltipTopOffset = 0;

    const backgroundOverlay = document.createElement("hr");
    backgroundOverlay.className = "u-background-overlay";

    const tooltip = document.createElement("div");
    tooltip.className = "u-tooltip";

    let seriesIdx: any = null;
    let dataIdx: any = null;

    let over: any;

    let tooltipVisible = false;

    function showTooltip() {
        if (!tooltipVisible) {
            tooltip.style.display = "block";
            backgroundOverlay.style.display = "block";
            tooltipVisible = true;
        }
    }

    function hideTooltip() {
        if (tooltipVisible) {
            tooltip.style.display = "none";
            backgroundOverlay.style.display = "none";
            tooltipVisible = false;
        }
    }

    function setTooltip(u: any, dataIdx: number, seriesIdx: number) {
        let top = u.valToPos(u.data[seriesIdx][dataIdx], 'y');
        let lft = u.valToPos(u.data[0][dataIdx], 'x');

        const timeInMilli = u.data[0][dataIdx] * 1000;
        const lengthOfSegment = u.data[seriesIdx + TIMELINE_PLOT_DATA_OFFSET][dataIdx][2];
        const description = u.data[seriesIdx + TIMELINE_PLOT_DATA_OFFSET][dataIdx][3];
        let widthOfSegment = u.valToPos(u.data[0][dataIdx] + lengthOfSegment, 'x') - lft;

        // Only show tooltip if very close to the point
        if (Math.abs(u.cursor.top - top) < 5 && Math.abs(u.cursor.left - lft) < 5) {
            showTooltip();

            let typeOfDatapoint;
            if (seriesIdx === INDEX_LAB_INDICATOR) {
                typeOfDatapoint = "Lab Collection";
            } else if (seriesIdx == INDEX_MED_INDICATOR) {
                typeOfDatapoint = "Medication Administration";
            } else if (seriesIdx == INDEX_ANNOTATION_INDICATOR) {
                typeOfDatapoint = "User Annotation";
            } else if (seriesIdx == INDEX_OTHER_INDICATOR) {
                typeOfDatapoint = "Other";
            }

            tooltip.style.top  = (tooltipTopOffset  + top + shiftY) + "px";
            backgroundOverlay.style.top = top + "px";

            if (Math.abs(lft - window.innerWidth) < 300) {
                // Too close to the right edge of browser so show tooltip left of cursor
                tooltip.style.left = (lft - 200 + shiftX) + "px";
            } else {
                tooltip.style.left = (tooltipLeftOffset + lft + shiftX) + "px";
            }

            if (lengthOfSegment > 0) {
                backgroundOverlay.style.left = (tooltipLeftOffset + lft + 6) + "px";
                backgroundOverlay.style.width = widthOfSegment + "px";
            } else {
                backgroundOverlay.style.left = "-1000px";
            }
            tooltip.innerHTML = `<div class='u-tooltip-header'>${typeOfDatapoint}</div><div class='u-tooltip-body'>${description}<br />${getSecondDateStrFromDateStr(timeInMilli)}${lengthOfSegment > 0 ? ` &bull; ${formatLineSegLen(lengthOfSegment)}` : ""}</div>`
        }
    }

    return {
        // @ts-ignore
        hooks: {
            ready: [
                (u: any) => {
                    over = u.over;

					over.addEventListener("mouseleave", () => {
						if (!u.cursor._lock) {
							hideTooltip();
						}
					});

                    tooltipLeftOffset = parseFloat(over.style.left);
                    tooltipTopOffset = parseFloat(over.style.top);
                    u.root.querySelector(".u-wrap").appendChild(tooltip);
                    u.root.querySelector(".u-wrap").appendChild(backgroundOverlay);

                    let clientX: any;
                    let clientY: any;

                    over.addEventListener("mousedown", (e: any) => {
                        clientX = e.clientX;
                        clientY = e.clientY;
                    });

                    over.addEventListener("mouseup", (e: any) => {
                        // clicked in-placemouseup instead of a drag event
                        if (e.clientX == clientX && e.clientY == clientY) {
                            if (seriesIdx != null && dataIdx != null) {
                                // @ts-ignore
                                globalThis.zoomedRange = {
                                    min: u.scales.x.min,
                                    max: u.scales.x.max,
                                }

                                let lengthInSec = u.data[seriesIdx + TIMELINE_PLOT_DATA_OFFSET][dataIdx][2];
                                let label = u.data[seriesIdx + TIMELINE_PLOT_DATA_OFFSET][dataIdx][3];
                                let top = u.valToPos(u.data[seriesIdx][dataIdx], 'y');
                                let lft = u.valToPos(u.data[        0][dataIdx], 'x');
                                const x = u.data[0][dataIdx];
                                // Only open modal if very close to the point
                                if (Math.abs(u.cursor.top - top) < 5 && Math.abs(u.cursor.left - lft) < 5) {
                                    if (seriesIdx === INDEX_ANNOTATION_INDICATOR) {
                                        // Set the value that the user clicked on but directly
                                        // show the annotation modal to allow users to edit it
                                        handleShowContextMenu(e, false, x);
                                        handleShowUserAnnotationModal(true);
                                    } else if (seriesIdx === INDEX_LAB_INDICATOR) {
                                        // If the annotation is a lab annotation, we want it to open the labs modal
                                        handleShowLabsModal(true, x);
                                    } else if (seriesIdx === INDEX_OTHER_INDICATOR) {
                                        handleShowOtherAnnotationModal(x);
                                    } else {
                                        let truncatedLabel = label.replaceAll("<br />", " | ");
                                        truncatedLabel = truncatedLabel.length >= 50 ? truncatedLabel.substring(0, 50) + "..." : truncatedLabel;
                                        addAnnotationToPlots(x, patientVisit, "eventMark", truncatedLabel, lengthInSec);
                                    }
                                    return;
                                }
                            }
                        }
                    });

                    over.addEventListener("contextmenu", (e: any) => {
                        let clickedTimestamp = u.posToVal(e.offsetX, "x");
                        // Round to nearest 60 because they have to start on the minute
                        clickedTimestamp = Math.round(clickedTimestamp / 60) * 60
                        handleShowContextMenu(e, true, clickedTimestamp);
                    });
                }
            ],
            setCursor: [
                (u: any) => {
                    let c = u.cursor;
                    if (u.cursor.top < 0 || u.cursor.left < 0) {
                        hideTooltip();
                    } else {
                        dataIdx = c.idx;
                        if (seriesIdx !== null && dataIdx !== null) {
                            setTooltip(u, dataIdx, seriesIdx);
                        } else {
                            hideTooltip();
                        }
                    }
                }
            ],
            setSeries: [
                (u: any, sidx: any) => {
                    // console.log("setSeries called sidx", sidx)
                    if (seriesIdx != sidx) {
                        seriesIdx = sidx;
                    }
                }
            ],
        }
    };
}

// Updated : Aug 16 2024
// Index for v2.0.10
export const DISPO_RISK_THRESHOLD = 0.53404;

export function tooltipRiskPlugin() {
    let seriestt: any;

    function init(u: any, opts: any, data: any) {
        let over = u.over;

        seriestt = document.createElement("div");
        seriestt.className = "u-tooltip";
        seriestt.textContent = "";
        seriestt.style.pointerEvents = "none";
        seriestt.style.position = "absolute";
        over.appendChild(seriestt);

        function hideTips() {
            seriestt.style.display = "none";
            seriestt.style.opacity = 0;
        }

        function showTips() {
            seriestt.style.display = "block";
            seriestt.style.opacity = 1;
        }

        over.addEventListener("mouseleave", () => {
            hideTips();
        });

        over.addEventListener("mouseenter", () => {
            showTips();
        });
    }

    function setCursor(u: any) {
        const {left, top, idx} = u.cursor;

        let xVal = u.data[0][idx];
        let predValue = u.data[1][idx];

        if (predValue > DISPO_RISK_THRESHOLD) {
            seriestt.innerHTML = `
                <div class='u-tooltip-header'>${getSecondDateStrFromDateStr(xVal * 1000)}</div>
                <div class='u-tooltip-body'>
                    Prediction: ${predValue ? predValue.toFixed(2) : ""}
                    <span class="badge bg-danger">
                        High Risk
                    </span>
                </div>
            `
        } else {
            seriestt.innerHTML = `
                <div class='u-tooltip-header'>${getSecondDateStrFromDateStr(xVal * 1000)}</div>
                <div class='u-tooltip-body'>
                    Prediction: ${predValue ? predValue.toFixed(2): ""} 
                    <span class="badge bg-info">
                        Low Risk
                    </span>
                </div>
            `
        }

        seriestt.style.left = Math.round(u.valToPos(xVal, 'x') + 10) + "px";
        seriestt.style.top = "10px";
    }

    return {
        hooks: {
            init,
            setCursor,
            setScale: [
                (u: any, key: any) => {
                    // console.log('setScale', key);
                }
            ],
            setSeries: [
                (u: any, idx: any) => {
                    // console.log('setSeries', idx);
                }
            ],
        },
    };
}

export function tooltipNumericPlugin() {
    let seriestt: any;

    function init(u: any, opts: any, data: any) {
        let over = u.over;

        seriestt = document.createElement("div");
        seriestt.className = "u-tooltip";
        seriestt.textContent = "";
        seriestt.style.pointerEvents = "none";
        seriestt.style.position = "absolute";
        over.appendChild(seriestt);

        function hideTips() {
            seriestt.style.display = "none";
            seriestt.style.opacity = 0;
        }

        function showTips() {
            seriestt.style.display = "block";
            seriestt.style.opacity = 1;
        }

        over.addEventListener("mouseleave", () => {
            hideTips();
        });

        over.addEventListener("mouseenter", () => {
            showTips();
        });
    }

    function formatVitalSign(vital: string, idx: number, shouldBold?: boolean) {

        // @ts-ignore
        const numericsData: any = window.numericsData;

        if (!numericsData || !numericsData[vital]) {
            return "";
        }

        let val1: any = numericsData[vital][3][idx];
        let val2: any = numericsData[vital][4][idx];
        let chartedVal1: any = numericsData[vital][1][idx];
        let chartedVal2: any = numericsData[vital][2][idx];

        let chartedSuffix = "";

        if (val2 && chartedVal1 && chartedVal2 && val2 !== chartedVal2) {
            chartedSuffix = `(Charted ${chartedVal1.toFixed(0)}/${chartedVal2.toFixed(0)})`
        } else if (val1 && chartedVal1 && val1 !== chartedVal1) {
            chartedSuffix = `(Charted ${chartedVal1.toFixed(2)})`
        }

        if (shouldBold) {
            if (val1 && val2) {
                return `<strong>${vital}</strong>: ${val1.toFixed(0)}/${val2.toFixed(0)} ${chartedSuffix}<br />`
            } else if (val1) {
                return `<strong>${vital}</strong>: ${val1.toFixed(2)} ${chartedSuffix}<br />`
            } else if (chartedVal1 && chartedVal2) {
                return `<strong>${vital}</strong>: ${chartedVal1}/${chartedVal2}<br />`
            } else if (chartedVal1) {
                return `<strong>${vital}</strong>: ${chartedVal1.toFixed(2)}<br />`
            } else {
                return ""
            }
        } else {
            if (val1 && val2) {
                return `${vital}: ${val1.toFixed(0)}/${val2.toFixed(0)} ${chartedSuffix}<br />`
            } else if (val1) {
                return `${vital}: ${val1.toFixed(2)} ${chartedSuffix}<br />`
            } else if (chartedVal1 && chartedVal2) {
                return `${vital}: ${chartedVal1}/${chartedVal2}<br />`
            } else if (chartedVal1) {
                return `${vital}: ${chartedVal1.toFixed(2)}<br />`
            } else {
                return ""
            }
        }
    }

    function setCursor(u: any) {
        const {left, top, idx} = u.cursor;

        const vital = u.root.id.split("-")[1];
        let xVal = u.data[0][idx];
        let yVal = null;
        let s = null;
        for (let i = 1; i < u.data.length; i++) {
            if (u.data[i][idx]) {
                yVal = u.data[i][idx];
                s = u.series[i];
            }
        }

        if (yVal) {
            seriestt.innerHTML = `
                <div class='u-tooltip-header'>${getSecondDateStrFromDateStr(xVal * 1000)}</div>
                <div class='u-tooltip-body'>
                    ${formatVitalSign("HR", idx, "HR" === vital)}
                    ${formatVitalSign("RR", idx, "RR" === vital)}
                    ${formatVitalSign("SpO2", idx, "SpO2" === vital)}
                    ${formatVitalSign("NBP", idx, "NBP" === vital)}
                    ${formatVitalSign("Temp", idx, "Temp" === vital)}
                    ${formatVitalSign("Perf", idx, "Perf" === vital)}
                </div>
            `

            seriestt.style.left = Math.round(u.valToPos(xVal, 'x') + 10) + "px";
            seriestt.style.top = Math.round(u.valToPos(yVal, s.scale) + 10) + "px";
        }
    }

    return {
        hooks: {
            init,
            setCursor,
            setScale: [
                (u: any, key: any) => {
                    // console.log('setScale', key);
                }
            ],
            setSeries: [
                (u: any, idx: any) => {
                    // console.log('setSeries', idx);
                }
            ],
        },
    };
}

export function tooltipWaveformAvailabilityPlugin() {
    let seriestt: any;

    function init(u: any, opts: any, data: any) {
        let over = u.over;

        seriestt = document.createElement("div");
        seriestt.className = "u-tooltip";
        seriestt.textContent = "";
        seriestt.style.pointerEvents = "none";
        seriestt.style.position = "absolute";
        over.appendChild(seriestt);

        function hideTips() {
            seriestt.style.display = "none";
            seriestt.style.opacity = 0;
        }

        function showTips() {
            seriestt.style.display = "block";
            seriestt.style.opacity = 1;
        }

        over.addEventListener("mouseleave", () => {
            hideTips();
        });

        over.addEventListener("mouseenter", () => {
            showTips();
        });
    }

    function setCursor(u: any) {
        const {left, top, idx} = u.cursor;

        let xVal = u.data[0][idx];
        let iiQuality = u.data[1][idx] !== null ? true : false;
        let plethQuality = u.data[3][idx] !== null ? true : false;
        let respQuality = u.data[5][idx] !== null ? true : false;
        let iiClassification = u.data[8][idx] !== null ? ` (${u.data[8][idx]})` : "";

        seriestt.innerHTML = `
            <div class='u-tooltip-header'>${getSecondDateStrFromDateStr(xVal * 1000)}</div>
            <div class='u-tooltip-body'>
                II: ${iiQuality ? "✓" : "✗"}${iiClassification}<br />
                Pleth: ${plethQuality ? "✓" : "✗"}<br />
                Resp: ${respQuality ? "✓" : "✗"}
            </div>
        `

        seriestt.style.left = Math.round(u.valToPos(xVal, 'x') + 10) + "px";
        seriestt.style.top = "10px";
    }

    return {
        hooks: {
            init,
            setCursor,
            setScale: [
                (u: any, key: any) => {
                    // console.log('setScale', key);
                }
            ],
            setSeries: [
                (u: any, idx: any) => {
                    // console.log('setSeries', idx);
                }
            ],
        },
    };
}


export function wheelZoomPlugin(opts: any) {
    let factor = opts.factor || 0.75;

    let xMin: any, xMax: any, yMin: any, yMax: any, xRange: any, yRange: any;

    function clamp(nRange: any, nMin: any, nMax: any, fRange: any, fMin: any, fMax: any) {
        if (nRange > fRange) {
            nMin = fMin;
            nMax = fMax;
        }
        else if (nMin < fMin) {
            nMin = fMin;
            nMax = fMin + nRange;
        }
        else if (nMax > fMax) {
            nMax = fMax;
            nMin = fMax - nRange;
        }

        return [nMin, nMax];
    }

    return {
        hooks: {
            ready: (u: any) => {
                xMin = u.scales.x.min;
                xMax = u.scales.x.max;
                yMin = u.scales.y.min;
                yMax = u.scales.y.max;

                xRange = xMax - xMin;
                yRange = yMax - yMin;

                let over = u.over;
                let rect = over.getBoundingClientRect();

                // wheel drag pan
                over.addEventListener("mousedown", (e: any) => {
                    if (e.button == 1) {
                    //	plot.style.cursor = "move";
                        e.preventDefault();

                        let left0 = e.clientX;
                    //	let top0 = e.clientY;

                        let scXMin0 = u.scales.x.min;
                        let scXMax0 = u.scales.x.max;

                        let xUnitsPerPx = u.posToVal(1, 'x') - u.posToVal(0, 'x');

                        // @ts-ignore
                        function onmove(e: any) {
                            e.preventDefault();

                            let left1 = e.clientX;
                        //	let top1 = e.clientY;

                            let dx = xUnitsPerPx * (left1 - left0);

                            u.setScale('x', {
                                min: scXMin0 - dx,
                                max: scXMax0 - dx,
                            });
                        }

                        // @ts-ignore
                        function onup(e: any) {
                            document.removeEventListener("mousemove", onmove);
                            document.removeEventListener("mouseup", onup);
                        }

                        document.addEventListener("mousemove", onmove);
                        document.addEventListener("mouseup", onup);
                    }
                });

                // wheel scroll zoom
                over.addEventListener("wheel", (e: any) => {
                    e.preventDefault();

                    let {left, top} = u.cursor;

                    let leftPct = left/rect.width;
                    let btmPct = 1 - top/rect.height;
                    let xVal = u.posToVal(left, "x");
                    let yVal = u.posToVal(top, "y");
                    let oxRange = u.scales.x.max - u.scales.x.min;
                    let oyRange = u.scales.y.max - u.scales.y.min;

                    let nxRange = e.deltaY < 0 ? oxRange * factor : oxRange / factor;
                    let nxMin = xVal - leftPct * nxRange;
                    let nxMax = nxMin + nxRange;
                    [nxMin, nxMax] = clamp(nxRange, nxMin, nxMax, xRange, xMin, xMax);

                    let nyRange = e.deltaY < 0 ? oyRange * factor : oyRange / factor;
                    let nyMin = yVal - btmPct * nyRange;
                    let nyMax = nyMin + nyRange;
                    [nyMin, nyMax] = clamp(nyRange, nyMin, nyMax, yRange, yMin, yMax);

                    u.batch(() => {
                        u.setScale("x", {
                            min: nxMin,
                            max: nxMax,
                        });

                        var plots = uPlot.sync(u.cursor.sync.key).plots
                        plots.forEach(p=>{
                            p.setScale("x", {
                                min: nxMin,
                                max: nxMax,
                            })
                        })
                        // u.setScale("y", {
                        //     min: nyMin,
                        //     max: nyMax,
                        // });
                    });
                });
            }
        }
    };
}

/**
 * Truncates a string to display on the plot (to avoid having runaway strings being displayed)
 * Truncate when we reach first number (which usually is the med concentration) or the first opening bracket
 * @param source The string to display
 * @param size The number of characters to keep
 * @returns The truncated string, followed by ...
 */
export function truncate(sourceInput: string, size: number) {
    const source = sourceInput.split(/\(|\s\d/)[0];
    return source.length > size ? source.slice(0, size - 1) + "…" : source;
}

/**
 * Gets the text preview that will be displayed on the timeline
 * @param segments [
                      [
                          Start epoch time of segment,
                          End epoch time of segment,
                          Name of med or lab
                          Offset (to prevent overlap of meds that happen at the same time - only applies to the meds)
                      ] 
                  ]
*
 */
export function getTimelinePreviewText(segments: any[]) {
    let retText = "";
    if (segments.length > 0) {
        // Get the text from the first segment
        retText = truncate(segments[0][2], 24);
    }
    if (segments.length > 1) {
        retText += ` +${segments.length}`
    }
    return retText;
}

/**
 * Gets the text that will be displayed in the hover popover
 * @param segments [
                      [
                          Start epoch time of segment,
                          End epoch time of segment,
                          Name of med or lab
                          Offset (to prevent overlap of meds that happen at the same time - only applies to the meds)
                      ] 
                  ]
*
 */
export function getTimelinePopover(segments: any[], type: string) {
    if (segments.length === 0) {
        return "N/A"
    } else if (segments.length < 2) {
        return segments[0][2]
    } else if (type === "imaging") {
        return segments.map((seg) => seg[2]).join(", ");
    } else {
        return `${segments.length} ${type}`
    }
}

/**
 * Gets the length of the line segment that will be displayed on the timeline
 * @param segment
                      [
                          Start epoch time of segment,
                          End epoch time of segment,
                          Name of med or lab
                          Offset (to prevent overlap of meds that happen at the same time - only applies to the meds)
                      ] 
                  
 *
 */
export function getLineSegLen(segment: any) {
    return segment[1] - segment[0];
}

/**
 * Formats the segment length
 * @param length length in seconds
 * @returns "23 min" or "24 sec"
 */
export function formatLineSegLen(length: number) {
    if (length === 0) {
        return "N/A";
    } else if (length < 60) {
        return `${length} sec`;
    } else {
        return `${Math.round(length / 60)} min`;
    }
}

/**
 * Generates the x-axis timestamps for the waveform plots
 * @param offset The time which to start the timestamps generation
 * @param seconds The number of seconds to generate timestamps for
 * @param frequency The frequency of the waveform
 * @returns 
 */
export function generateTimestamps(offset: number, seconds: number, frequency: number) {
    const timestamps: number[] = [];
    const step = 1 / frequency;
    let z = offset;
    for (let i = 0; i < seconds; i++) {
        for (let j = 0; j < frequency; j++) {
            // timestamps.push(start + i * j);
            timestamps.push(z);
            z = z + step;
        }
    }

    return timestamps;
}

export function capWaveformVals(vals: number[], waveformType: string) {
    if (waveformType === "II") {
        const updatedVals: number[] = [];
        vals.forEach((element: number) => {
            if (element > 3) {
                updatedVals.push(3);
            } else if (element < -3) {
                updatedVals.push(-3);
            } else {
                updatedVals.push(element);
            }
        });
        return updatedVals;
    } else {
        return vals;
    }
}

/**
 * Returns true if the current numeric is the last visible numeric on the dashboard
 * This is used to determine if we need to show the x-axis
 */
export function isLastVisibleNumericsPlot(numerics: string[], numeric: any, numericsHiddenRef: any) {
    // ECG Numerics are in a separate box, so we ignore it for the purposes of this calculation
    for (let i = numerics.length - 1; i >= 0; i--) {
        if (numericsHiddenRef.current && numericsHiddenRef.current[numerics[i]]) {
            continue;
        } else if (numerics[i] === numeric) {
            // Last visible
            // console.log("Last numeric is visible")
            return true;
        } else {
            // console.log("Last numeric is NOT visible")
            return false;
        }
    }
    console.log("Default option")
    return false;
}

/**
 * Points on the timeline can get quite crowded. This function attempts to vertically separate
 * the points according to the height of the plot. 
 */
export function verticallySeparatePoints(overviewData: any[][], newHeight: number) {
    const updatedOverviewData = overviewData.map((innerArray: any) => [...innerArray]);
    // [
    //     [1647532140, 1647532320, 1647532440, ...],
    //     [null, 2, 1.5, 1, 0.5, 2, 1.5, 1, ...]
    //     ...
    // ]
    const numberOfRowSlotsAvailable = Math.max(Math.round(newHeight / 45), 4);
    console.log("numberOfRowSlotsAvailable", numberOfRowSlotsAvailable)
    let ringIndex = 0;
    const medsAdministered = [];
    const annotations = [];
    const others = [];
    for (let i = 0; i < updatedOverviewData[0].length; i++) {
        if (updatedOverviewData[INDEX_MED_INDICATOR][i] === null) {
            medsAdministered.push(null);
        } else {
            medsAdministered.push(2 - (ringIndex % numberOfRowSlotsAvailable) * (2 / numberOfRowSlotsAvailable));
            ringIndex++;
        }

        if (updatedOverviewData[INDEX_ANNOTATION_INDICATOR][i] === null) {
            annotations.push(null);
        } else {
            annotations.push(2 - (ringIndex % numberOfRowSlotsAvailable) * (2 / numberOfRowSlotsAvailable));
            ringIndex++;
        }

        if (updatedOverviewData[INDEX_OTHER_INDICATOR][i] === null) {
            others.push(null);
        } else {
            others.push(2 - (ringIndex % numberOfRowSlotsAvailable) * (2 / numberOfRowSlotsAvailable));
            ringIndex++;
        }
    }
    updatedOverviewData[INDEX_MED_INDICATOR] = medsAdministered;
    updatedOverviewData[INDEX_ANNOTATION_INDICATOR] = annotations;
    updatedOverviewData[INDEX_OTHER_INDICATOR] = others;
    return updatedOverviewData;
}
